// /tours/$id/edit — edit a saved tour route.
//
// Mirrors /tours/new: same shells, same form library, same CityLegList +
// corridor slider. Hydrates from the routes list (no per-id GET on the
// backend), title-cases stored city values for display, lets the shared
// zod schema re-lower-case on submit. Optimistic-lock 409s navigate
// back to the detail page rather than stay on a stale form.

import {
  Anchor,
  Button,
  Group,
  Loader,
  Paper,
  Slider,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  PLANNING_LIMITS,
  updateTourRouteInputSchema,
} from '@wwaave/shared/zod';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useEffect, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import {
  CityLegList,
  type CityLegValue,
} from '../../components/tours/CityLegList';
import { PageHero } from '../../components/ui';
import { ApiError, fetchOk } from '../../lib/api';
import { csrfHeaders } from '../../lib/auth-context';
import { showToast } from '../../lib/toast';

export const Route = createFileRoute('/tours/$id_/edit')({
  component: ToursEditPage,
});

type TourRouteCityLeg = {
  name: string;
  fromDate?: number;
  toDate?: number;
};

type TourRouteDTO = {
  id: string;
  name: string;
  cities: TourRouteCityLeg[];
  fromDate: number;
  toDate: number;
  isActive: boolean;
  notifyOnMatch: boolean;
  corridorRadiusKm: number;
  createdAt: number;
  updatedAt: number;
};

type FormValues = {
  name: string;
  cities: CityLegValue[];
  from_date: string;
  to_date: string;
  is_active: boolean;
  notify_on_match: boolean;
  corridor_radius_km: number;
};

// Stored cities are lower-cased server-side via a zod transform. Render them
// title-cased so the artist doesn't see "brisbane" / "byron bay" in inputs.
// The server re-lower-cases on submit, so casing is round-trip safe.
function titleCaseCity(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

// ms-epoch -> "YYYY-MM-DD" for the HTML date input.
function msToYmd(ms: number): string {
  const d = new Date(ms);
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0');
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const dd = d.getUTCDate().toString().padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const RADIUS_MARKS = [
  { value: 0, label: 'Exact city only' },
  { value: 50, label: '50 km' },
  { value: PLANNING_LIMITS.corridorRadiusMaxKm, label: 'Wide net' },
];

function ToursEditPage(): React.JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState<TourRouteDTO | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      cities: [{ name: '' }],
      from_date: '',
      to_date: '',
      is_active: true,
      notify_on_match: true,
      corridor_radius_km: PLANNING_LIMITS.corridorRadiusDefaultKm,
    },
    validate: zod4Resolver(updateTourRouteInputSchema),
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchOk('/api/me/tour-routes', { credentials: 'include' });
        const json = (await res.json()) as { items: TourRouteDTO[] };
        if (cancelled) return;
        const found = json.items.find((r) => r.id === id) ?? null;
        if (found === null) {
          setLoadError("This route isn't here. It may have been removed.");
          return;
        }
        setRoute(found);
        form.setValues({
          name: found.name,
          cities: found.cities.map((leg) => {
            const out: CityLegValue = { name: titleCaseCity(leg.name) };
            if (leg.fromDate !== undefined) out.from_date = msToYmd(leg.fromDate);
            if (leg.toDate !== undefined) out.to_date = msToYmd(leg.toDate);
            return out;
          }),
          from_date: msToYmd(found.fromDate),
          to_date: msToYmd(found.toDate),
          is_active: found.isActive,
          notify_on_match: found.notifyOnMatch,
          corridor_radius_km: found.corridorRadiusKm,
        });
        form.resetDirty();
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setLoadError("This route isn't here. It may have been removed.");
        } else {
          setLoadError(err instanceof ApiError ? err.message : "We couldn't load this route.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // form is a stable reference from useForm; depending on `id` only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function onSubmit(values: FormValues): Promise<void> {
    if (route === null) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = {
        name: values.name.trim(),
        cities: values.cities
          .filter((leg) => leg.name.trim().length > 0)
          .map((leg) => ({
            name: leg.name.trim(),
            ...(leg.from_date !== undefined && leg.from_date !== ''
              ? { from_date: leg.from_date }
              : {}),
            ...(leg.to_date !== undefined && leg.to_date !== ''
              ? { to_date: leg.to_date }
              : {}),
          })),
        from_date: values.from_date,
        to_date: values.to_date,
        is_active: values.is_active,
        notify_on_match: values.notify_on_match,
        corridor_radius_km: values.corridor_radius_km,
      };
      const res = await fetchOk(`/api/me/tour-routes/${encodeURIComponent(route.id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify(body),
      });
      await res.json().catch(() => null);
      showToast({ kind: 'success', message: 'Route saved.' });
      navigate({ to: '/tours/$id', params: { id: route.id } });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        showToast({
          kind: 'warning',
          message: 'This route changed in another tab. Please review and try again.',
        });
        navigate({ to: '/tours/$id', params: { id: route.id } });
        return;
      }
      const message =
        err instanceof ApiError ? err.message : "We couldn't save that route.";
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError !== null) {
    return (
      <AppShell tone="form">
        <Stack gap="md" py="xl">
          <Text c="red" role="alert">
            {loadError}
          </Text>
          <Anchor component={Link} to="/tours">
            Back to routes
          </Anchor>
        </Stack>
      </AppShell>
    );
  }

  if (route === null) {
    return (
      <AppShell tone="form">
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </AppShell>
    );
  }

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <PageHero
          eyebrow="Tour routes"
          title="Edit your route"
          lede="Tweak the cities, the window, or pause matches when you're off the road."
          action={
            <Link
              to="/tours/$id"
              params={{ id: route.id }}
              style={{ textDecoration: 'none' }}
            >
              <Anchor component="span" size="sm">
                Back to route
              </Anchor>
            </Link>
          }
        />

        <Paper withBorder p="lg" radius="md">
          <form
            onSubmit={form.onSubmit((values) => {
              void onSubmit(values);
            })}
          >
            <Stack gap="md">
              <TextInput
                label="Route name"
                placeholder="East coast, late winter"
                description="Just for you. For example, 'East coast, late winter'."
                maxLength={PLANNING_LIMITS.tourRouteNameMax}
                required
                {...form.getInputProps('name')}
              />

              <Stack gap={4}>
                <Text fw={500} size="sm">
                  Cities, in order
                </Text>
                <Text size="xs" c="dimmed">
                  Add {PLANNING_LIMITS.tourRouteMinCities}–
                  {PLANNING_LIMITS.tourRouteMaxCities}. Use the arrows to reorder.
                </Text>
                <CityLegList
                  value={form.values.cities}
                  onChange={(next) => form.setFieldValue('cities', next)}
                  routeFrom={form.values.from_date}
                  routeTo={form.values.to_date}
                  error={form.errors.cities}
                />
              </Stack>

              <Group grow>
                <TextInput
                  type="date"
                  label="From"
                  required
                  {...form.getInputProps('from_date')}
                />
                <TextInput
                  type="date"
                  label="To"
                  required
                  {...form.getInputProps('to_date')}
                />
              </Group>

              <Stack gap={4}>
                <Text fw={500} size="sm">
                  Corridor radius: {form.values.corridor_radius_km} km
                </Text>
                <Text size="xs" c="dimmed">
                  We'll surface venues this far from each city on your route.
                </Text>
                <Slider
                  min={PLANNING_LIMITS.corridorRadiusMinKm}
                  max={PLANNING_LIMITS.corridorRadiusMaxKm}
                  step={5}
                  marks={RADIUS_MARKS}
                  value={form.values.corridor_radius_km}
                  onChange={(v) => form.setFieldValue('corridor_radius_km', v)}
                  mb="xl"
                />
              </Stack>

              <Switch
                label={form.values.is_active ? 'Active' : 'Paused'}
                description="Pause to stop match notifications without removing the route."
                {...form.getInputProps('is_active', { type: 'checkbox' })}
              />

              <Switch
                label="Send me a notification when something matches"
                description="You can change this any time."
                {...form.getInputProps('notify_on_match', { type: 'checkbox' })}
              />

              {submitError !== null ? (
                <Text c="red" role="alert">
                  {submitError}
                </Text>
              ) : null}

              <Group justify="flex-end" gap="sm">
                <Button
                  type="button"
                  variant="subtle"
                  onClick={() =>
                    navigate({ to: '/tours/$id', params: { id: route.id } })
                  }
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={submitting}
                  variant="gradient"
                  gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                >
                  Save changes
                </Button>
              </Group>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </AppShell>
  );
}
